import React, { useState, useCallback } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Scan } from 'lucide-react';
import api from '../services/api';

const supportsPtz = (camera) =>
  camera &&
  (camera.type === 'isapi' || (camera.manufacturer && camera.manufacturer.toLowerCase() === 'dahua'));

const sendPtz = async (cameraId, command, action) => {
  try {
    await api.post(`/cameras/${cameraId}/ptz`, { command, action });
  } catch (err) {
    console.warn('PTZ command failed:', err.response?.data?.message || err.message);
    return err.response?.data?.message || 'PTZ failed';
  }
  return null;
};

/**
 * PTZ control panel for Dahua/ISAPI cameras. Press and hold to move; release to stop.
 * Renders nothing if camera doesn't support PTZ.
 */
const PtzControls = ({ camera, className = '' }) => {
  const [ptzError, setPtzError] = useState(null);

  const handleStart = useCallback(
    (command) => {
      if (!camera?._id) return;
      setPtzError(null);
      sendPtz(camera._id, command, 'start').then((msg) => msg && setPtzError(msg));
    },
    [camera?._id]
  );

  const handleStop = useCallback(
    (command) => {
      if (!camera?._id) return;
      sendPtz(camera._id, command, 'stop').catch(() => {});
    },
    [camera?._id]
  );

  if (!camera || !supportsPtz(camera) || !camera.isOnline || !camera.isActive) {
    return null;
  }

  const stopVideoInteraction = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const btn = (command, Icon, label) => (
    <button
      type="button"
      className="flex items-center justify-center w-10 h-9 bg-white/15 hover:bg-white/25 rounded-lg text-white transition-colors"
      title={label}
      onClick={stopVideoInteraction}
      onMouseDown={(e) => { stopVideoInteraction(e); handleStart(command); }}
      onMouseUp={(e) => { stopVideoInteraction(e); handleStop(command); }}
      onMouseLeave={(e) => { stopVideoInteraction(e); handleStop(command); }}
      onTouchStart={(e) => { stopVideoInteraction(e); handleStart(command); }}
      onTouchEnd={(e) => { stopVideoInteraction(e); handleStop(command); }}
    >
      <Icon className="h-5 w-5" />
    </button>
  );

  return (
    <div
      className={`absolute right-3 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 p-2 rounded-xl bg-black/75 backdrop-blur-sm z-20 ${className}`}
      onClick={stopVideoInteraction}
      onMouseDown={stopVideoInteraction}
      onMouseUp={stopVideoInteraction}
    >
      {ptzError && (
        <p className="text-red-400 text-xs max-w-[120px] truncate mb-1" title={ptzError}>
          {ptzError}
        </p>
      )}
      <p className="text-white/70 text-xs mb-1">PTZ</p>
      {/* Up */}
      <div className="flex justify-center">{btn('Up', ChevronUp, 'Tilt up')}</div>
      {/* Left - Center - Right */}
      <div className="flex items-center gap-1">
        {btn('Left', ChevronLeft, 'Pan left')}
        <div className="w-10 h-9 rounded-lg bg-transparent" />
        {btn('Right', ChevronRight, 'Pan right')}
      </div>
      {/* Down */}
      <div className="flex justify-center">{btn('Down', ChevronDown, 'Tilt down')}</div>
      {/* Zoom */}
      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/20">
        {btn('ZoomIn', ZoomIn, 'Zoom in')}
        {btn('ZoomOut', ZoomOut, 'Zoom out')}
      </div>
      {/* Focus */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="flex items-center justify-center w-8 h-8 bg-white/10 hover:bg-white/20 rounded text-white transition-colors"
          title="Focus near"
          onClick={stopVideoInteraction}
          onMouseDown={(e) => { stopVideoInteraction(e); handleStart('FocusNear'); }}
          onMouseUp={(e) => { stopVideoInteraction(e); handleStop('FocusNear'); }}
          onMouseLeave={(e) => { stopVideoInteraction(e); handleStop('FocusNear'); }}
          onTouchStart={(e) => { stopVideoInteraction(e); handleStart('FocusNear'); }}
          onTouchEnd={(e) => { stopVideoInteraction(e); handleStop('FocusNear'); }}
        >
          <Scan className="h-4 w-4" />
        </button>
        <button
          type="button"
          className="flex items-center justify-center w-8 h-8 bg-white/10 hover:bg-white/20 rounded text-white transition-colors"
          title="Focus far"
          onClick={stopVideoInteraction}
          onMouseDown={(e) => { stopVideoInteraction(e); handleStart('FocusFar'); }}
          onMouseUp={(e) => { stopVideoInteraction(e); handleStop('FocusFar'); }}
          onMouseLeave={(e) => { stopVideoInteraction(e); handleStop('FocusFar'); }}
          onTouchStart={(e) => { stopVideoInteraction(e); handleStart('FocusFar'); }}
          onTouchEnd={(e) => { stopVideoInteraction(e); handleStop('FocusFar'); }}
        >
          <Scan className="h-4 w-4 rotate-180" />
        </button>
      </div>
    </div>
  );
};

export default PtzControls;
export { supportsPtz };
